import type { RoundListItem } from "@dishour/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { unwrap } from "./client.js";

export const roundsKey = ["rounds"] as const;

export function useRounds() {
  return useQuery<RoundListItem[]>({
    queryKey: roundsKey,
    queryFn: () => fetch("/api/rounds", { credentials: "same-origin" }).then(unwrap<RoundListItem[]>),
  });
}

function mutateRound(path: string, method: "POST" | "DELETE", body?: unknown) {
  return fetch(path, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? {} : { "content-type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }).then(unwrap<RoundListItem>);
}

/** All three mutations get the updated round back directly and overwrite the cache — no refetch round-trip. */
function useRoundMutation<TVars>(makeRequest: (vars: TVars) => Promise<RoundListItem>) {
  const queryClient = useQueryClient();
  return useMutation<RoundListItem, Error, TVars>({
    mutationFn: makeRequest,
    onSuccess: (updated) => {
      queryClient.setQueryData<RoundListItem[]>(roundsKey, (prev) =>
        prev?.map((r) => (r.id === updated.id ? updated : r)),
      );
    },
  });
}

export const useCommit = (roundId: string) =>
  useRoundMutation<string>((qty) => mutateRound(`/api/rounds/${roundId}/commit`, "POST", { qty }));

export const useConfirmCommitment = (roundId: string) =>
  useRoundMutation<void>(() => mutateRound(`/api/rounds/${roundId}/confirm`, "POST"));

export const useWithdraw = (roundId: string) =>
  useRoundMutation<void>(() => mutateRound(`/api/rounds/${roundId}/commit`, "DELETE"));
